import globalConfig from '@utiles/officialStore/globalConfig';
import { getGlobalGetLinkParams } from '@utiles/globalGetLink';
import { getFlowBreakReason, isFlowInterrupted, isGamePaySource } from '@utiles/officialTopupNew/flowExtra';
import {
  bindFlowLifecycleListeners,
  getNavigationType,
  isNavigationTraverseEvent,
  isPageShowPersisted,
  safeSessionStorageGetItem,
  safeSessionStorageSetItem,
} from '@utiles/officialTopupNew/flowLifecycleCompat';

export {
  enrichEventWithFlowExtra,
  getFlowExtra,
  isFlowInterrupted,
  getFlowBreakReason,
  isGamePaySource,
} from '@utiles/officialTopupNew/flowExtra';

export const FLOW_BREAK_REASON = {
  RESULT_RECHARGE_AGAIN: 'result_recharge_again',
  PAGE_REFRESH: 'page_refresh',
  BROWSER_BACK_BFCACHE: 'browser_back_bfcache',
  BROWSER_BACK_NAVIGATE: 'browser_back_navigate',
  NEED_LOGIN: 'need_login',
  MANUAL_SELECT_ROLE: 'manual_select_role',
  PRODUCT_MATCH_FAIL: 'product_match_fail',
  MANUAL_SELECT_PRODUCT: 'manual_select_product',
  NO_AVAILABLE_PRODUCTS: 'no_available_products',
  PWA_ENTRY: 'pwa_entry',
};

const FLOW_INTERRUPTED_PARAM = 'flowInterrupted';
const FLOW_BREAK_REASON_PARAM = 'flowBreakReason';
const MINI_FALLBACK_V35_PREFIX = 'mini_fallback_v35_';

let isLifecycleListenerBound = false;

export const buildFallbackFlowBreakReason = (position) => {
  return `${MINI_FALLBACK_V35_PREFIX}${position || 'unknown'}`;
};

export const markFlowInterrupted = (reason) => {
  if (window.flowInterrupted) {
    return;
  }
  window.flowInterrupted = true;
  window.flowBreakReason = reason || '';
};

const shouldTrackFlow = () => {
  return isGamePaySource() && !isFlowInterrupted();
};

const getFlowStorageKey = (gameId, roleId) => {
  return `gamePayFlow_${gameId}_${roleId || '0'}`;
};

const getLinkFlowId = () => {
  const linkParams = getGlobalGetLinkParams() || {};
  return linkParams.flowId || globalConfig.getHrefParam('flowId') || '';
};

const getCurrentFlowContext = () => {
  return {
    gameId: globalConfig.getHrefParam('gameId') || '',
    roleId: globalConfig.getHrefParam('roleId') || '',
    flowId: getLinkFlowId(),
    srtl: globalConfig.getHrefParam('srtl') || '',
  };
};

const isPayHomeRoute = () => {
  const raw = (window.location.hash || '').split('?')[0];
  if (raw === '' || raw === '#') {
    return true;
  }
  const path = raw.replace(/^#/, '');
  return path === '' || path === '/';
};

const getStoredFlowContext = (key) => {
  try {
    return JSON.parse(safeSessionStorageGetItem(key) || 'null');
  } catch {
    return null;
  }
};

const isSameFlowContext = (stored, current) => {
  if (!stored || !current) {
    return false;
  }
  const gameId = current.gameId || '';
  const roleId = current.roleId || '';
  const flowId = current.flowId || '';
  if (!gameId || !roleId || !flowId) {
    return false;
  }
  return (
    String(stored.gameId || '') === String(gameId) &&
    String(stored.roleId || '') === String(roleId) &&
    String(stored.flowId || '') === String(flowId)
  );
};

const updateSessionStorageFlow = () => {
  if (!isGamePaySource()) {
    return;
  }
  const current = getCurrentFlowContext();
  if (!current.gameId) {
    return;
  }
  const key = getFlowStorageKey(current.gameId, current.roleId);
  const stored = getStoredFlowContext(key);
  safeSessionStorageSetItem(
    key,
    JSON.stringify({
      ...current,
      flowId: getLinkFlowId() || stored?.flowId || '',
      timestamp: Date.now(),
    })
  );
};

const detectFlowInterruptionFromStorage = () => {
  if (!shouldTrackFlow()) {
    return;
  }
  const current = getCurrentFlowContext();
  if (!current.gameId) {
    return;
  }
  const key = getFlowStorageKey(current.gameId, current.roleId);
  const stored = getStoredFlowContext(key);
  if (!stored) {
    return;
  }
  if (isSameFlowContext(stored, current)) {
    markFlowInterrupted(FLOW_BREAK_REASON.PAGE_REFRESH);
  }
};

const detectFlowInterruptionFromNavigation = () => {
  if (!shouldTrackFlow()) {
    return;
  }
  const navType = getNavigationType();
  if (navType === 'back_forward') {
    markFlowInterrupted(FLOW_BREAK_REASON.BROWSER_BACK_NAVIGATE);
  }
};

const detectFlowInterruptionFromPwaEntry = () => {
  if (!shouldTrackFlow()) {
    return;
  }
  if (globalConfig.getHrefParam('mode') !== 'pwa') {
    return;
  }
  markFlowInterrupted(FLOW_BREAK_REASON.PWA_ENTRY);
};

const handleFlowPageShow = (event) => {
  if (!isPageShowPersisted(event) || !shouldTrackFlow()) {
    return;
  }
  markFlowInterrupted(FLOW_BREAK_REASON.BROWSER_BACK_BFCACHE);
};

const handleFlowPopState = () => {
  if (!shouldTrackFlow() || !isPayHomeRoute()) {
    return;
  }
  markFlowInterrupted(FLOW_BREAK_REASON.BROWSER_BACK_NAVIGATE);
};

const handleFlowNavigate = (event) => {
  if (!shouldTrackFlow()) {
    return;
  }
  if (!isNavigationTraverseEvent(event)) {
    return;
  }
  if (!isPayHomeRoute()) {
    return;
  }
  markFlowInterrupted(FLOW_BREAK_REASON.BROWSER_BACK_NAVIGATE);
};

const setupFlowLifecycleListeners = () => {
  if (isLifecycleListenerBound || typeof window === 'undefined') {
    return;
  }
  isLifecycleListenerBound = true;
  bindFlowLifecycleListeners({
    onPageShow: handleFlowPageShow,
    onPopState: handleFlowPopState,
    onNavigate: handleFlowNavigate,
  });
};

export const appendFlowParamsToUrl = (url, { flowBreakReason } = {}) => {
  if (!url || !flowBreakReason) {
    return url;
  }
  const params = new URLSearchParams();
  params.set(FLOW_INTERRUPTED_PARAM, '1');
  params.set(FLOW_BREAK_REASON_PARAM, flowBreakReason);
  const paramStr = params.toString();

  const hashIndex = url.indexOf('#');
  if (hashIndex === -1) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${paramStr}`;
  }

  // 参数必须拼在 # 之前，否则 getHrefParam 无法从 location.search 读取
  const beforeHash = url.slice(0, hashIndex);
  const hashPart = url.slice(hashIndex);
  const separator = beforeHash.includes('?') ? '&' : '?';
  return `${beforeHash}${separator}${paramStr}${hashPart}`;
};

export const appendFlowParamsToSearchParams = (params = {}, flowBreakReason) => {
  if (!flowBreakReason) {
    return params;
  }
  return {
    ...params,
    [FLOW_INTERRUPTED_PARAM]: '1',
    [FLOW_BREAK_REASON_PARAM]: flowBreakReason,
  };
};

const restoreFlowStateFromUrl = () => {
  if (globalConfig.getHrefParam(FLOW_INTERRUPTED_PARAM) !== '1') {
    return;
  }
  window.flowInterrupted = true;
  window.flowBreakReason = getFlowBreakReason();
};

export const initFlowTrack = () => {
  restoreFlowStateFromUrl();
  detectFlowInterruptionFromPwaEntry();
  detectFlowInterruptionFromNavigation();
  detectFlowInterruptionFromStorage();
  updateSessionStorageFlow();
  setupFlowLifecycleListeners();
};
